// src/core/redux/services/adminSupportApi.ts
//
// The support console's write surface: impersonation grants, data repairs,
// support cases and platform announcements.
//
// These are the endpoints that DO things to a tenant, so the shapes here mirror
// the backend's guardrails rather than smoothing them away — `reason` is
// required, `dryRun` is a first-class argument, and `caseId` is threaded
// through so an action can be attributed to the case that justified it.

import { baseApi } from './baseApi';

// ── Impersonation ──────────────────────────────────────────────────────────

export interface ImpersonationGrant {
  token: string;
  expiresAt: string;
  readOnly: boolean;
  session: {
    id: string;
    targetUserId: string;
    targetEmail: string;
    targetTenantId: string;
    targetTenantName: string;
    reason: string;
    ticketRef?: string | null;
  };
}

export interface ImpersonationSession {
  id: string;
  impersonatorEmail: string;
  targetUserId: string;
  targetTenantId: string;
  reason: string;
  ticketRef?: string | null;
  readOnly: boolean;
  createdAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  caseId?: string | null;
}

// ── Support cases ──────────────────────────────────────────────────────────

export interface SupportCase {
  id: string;
  reference: string;
  tenantId: string;
  title: string;
  description?: string | null;
  severity: string;
  status: string;
  openedByEmail: string;
  requestIds: string[];
  createdAt: string;
  resolvedAt?: string | null;
  _count?: { events: number };
}

export interface SupportCaseEvent {
  id: string;
  type: string;
  actorEmail?: string | null;
  body?: string | null;
  data?: unknown;
  createdAt: string;
}

/** The review view: everything done under one case. */
export interface SupportCaseDetail {
  case: SupportCase & { events: SupportCaseEvent[] };
  tenant: { id: string; name: string; type?: string } | null;
  impersonations: ImpersonationSession[];
  exemptions: any[];
  linkedActivity: any[];
}

export const adminSupportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Impersonation ──
    grantImpersonation: builder.mutation<
      ImpersonationGrant,
      { targetTenantId: string; targetUserId?: string; reason: string; ticketRef?: string; caseId?: string; acknowledgeClinicalAccess?: boolean }
    >({
      query: (body) => ({ url: 'admin/impersonation', method: 'POST', body }),
      invalidatesTags: [{ type: 'Impersonation', id: 'LIST' }],
    }),
    listImpersonations: builder.query<{ data: ImpersonationSession[]; meta: any }, { tenantId?: string; active?: boolean } | void>({
      query: (q) => {
        const p = new URLSearchParams();
        if (q && (q as any).tenantId) p.set('tenantId', (q as any).tenantId);
        if (q && (q as any).active) p.set('active', 'true');
        const s = p.toString();
        return `admin/impersonation${s ? `?${s}` : ''}`;
      },
      providesTags: [{ type: 'Impersonation', id: 'LIST' }],
    }),
    revokeImpersonation: builder.mutation<any, string>({
      query: (id) => ({ url: `admin/impersonation/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Impersonation', id: 'LIST' }],
    }),

    // ── Repairs ──
    reconcileStockLedger: builder.mutation<
      any,
      { tenantId: string; tenantProductVariantId: string; storeId?: string | null; warehouseId?: string | null; reason: string; ticketRef?: string; caseId?: string; dryRun: boolean }
    >({
      query: (body) => ({ url: 'admin/repairs/stock/reconcile-ledger', method: 'POST', body }),
      // Only a real (non-dry-run) apply changes anything worth refetching.
      invalidatesTags: (_r, _e, arg) => (arg.dryRun ? [] : [{ type: 'Tenant' as const, id: arg.tenantId }]),
    }),
    unstickPurchaseOrder: builder.mutation<
      any,
      { tenantId: string; purchaseOrderId: string; action: 'cancel' | 'complete'; reason: string; ticketRef?: string; caseId?: string; dryRun: boolean }
    >({
      query: (body) => ({ url: 'admin/repairs/purchase-order/unstick', method: 'POST', body }),
      invalidatesTags: (_r, _e, arg) => (arg.dryRun ? [] : [{ type: 'Tenant' as const, id: arg.tenantId }]),
    }),
    reversePayment: builder.mutation<
      any,
      { tenantId: string; paymentId: string; reason: string; ticketRef?: string; caseId?: string; dryRun: boolean }
    >({
      query: (body) => ({ url: 'admin/repairs/payment/reverse', method: 'POST', body }),
      invalidatesTags: (_r, _e, arg) => (arg.dryRun ? [] : [{ type: 'Tenant' as const, id: arg.tenantId }]),
    }),
    releasePrescription: builder.mutation<
      any,
      { tenantId: string; prescriptionId: string; reason: string; ticketRef?: string; caseId?: string; dryRun: boolean }
    >({
      query: (body) => ({ url: 'admin/repairs/prescription/release', method: 'POST', body }),
      invalidatesTags: (_r, _e, arg) => (arg.dryRun ? [] : [{ type: 'Tenant' as const, id: arg.tenantId }]),
    }),
    repriceVariant: builder.mutation<
      any,
      { tenantId: string; tenantProductVariantId: string; unitPrice?: number; costPrice?: number; reason: string; ticketRef?: string; caseId?: string; dryRun: boolean }
    >({
      query: (body) => ({ url: 'admin/repairs/catalog/reprice-variant', method: 'POST', body }),
      invalidatesTags: (_r, _e, arg) => (arg.dryRun ? [] : [{ type: 'Tenant' as const, id: arg.tenantId }]),
    }),
    grantKycExemption: builder.mutation<
      any,
      { tenantId: string; reason: string; ticketRef?: string; caseId?: string; expiresInHours?: number; dryRun: boolean }
    >({
      query: (body) => ({ url: 'admin/repairs/kyc-exemption', method: 'POST', body }),
      invalidatesTags: (_r, _e, arg) => (arg.dryRun ? [] : [{ type: 'KycExemption' as const, id: 'LIST' }, { type: 'Tenant' as const, id: arg.tenantId }]),
    }),
    listKycExemptions: builder.query<{ data: any[] }, { tenantId?: string; active?: boolean } | void>({
      query: (q) => {
        const p = new URLSearchParams();
        if (q && (q as any).tenantId) p.set('tenantId', (q as any).tenantId);
        if (q && (q as any).active) p.set('active', 'true');
        const s = p.toString();
        return `admin/repairs/kyc-exemption${s ? `?${s}` : ''}`;
      },
      providesTags: [{ type: 'KycExemption', id: 'LIST' }],
    }),
    revokeKycExemption: builder.mutation<any, string>({
      query: (id) => ({ url: `admin/repairs/kyc-exemption/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'KycExemption', id: 'LIST' }],
    }),

    // ── Support cases ──
    listSupportCases: builder.query<{ data: SupportCase[]; meta: any }, { tenantId?: string; status?: string; page?: number; limit?: number } | void>({
      query: (q) => {
        const p = new URLSearchParams();
        Object.entries((q as any) ?? {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
        const s = p.toString();
        return `admin/support-cases${s ? `?${s}` : ''}`;
      },
      providesTags: [{ type: 'SupportCase', id: 'LIST' }],
    }),
    getSupportCase: builder.query<SupportCaseDetail, string>({
      query: (id) => `admin/support-cases/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SupportCase', id }],
    }),
    createSupportCase: builder.mutation<SupportCase, { tenantId: string; title: string; description?: string; severity?: string; requestIds?: string[] }>({
      query: (body) => ({ url: 'admin/support-cases', method: 'POST', body }),
      invalidatesTags: [{ type: 'SupportCase', id: 'LIST' }],
    }),
    updateSupportCase: builder.mutation<SupportCase, { id: string; status?: string; severity?: string; title?: string; description?: string }>({
      query: ({ id, ...body }) => ({ url: `admin/support-cases/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'SupportCase', id }, { type: 'SupportCase', id: 'LIST' }],
    }),
    addCaseNote: builder.mutation<any, { id: string; body: string }>({
      query: ({ id, body }) => ({ url: `admin/support-cases/${id}/notes`, method: 'POST', body: { body } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'SupportCase', id }],
    }),
    linkCaseRequestId: builder.mutation<any, { id: string; requestId: string }>({
      query: ({ id, requestId }) => ({ url: `admin/support-cases/${id}/request-ids`, method: 'POST', body: { requestId } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'SupportCase', id }],
    }),
    notifyTenantFromCase: builder.mutation<any, { id: string; message: string; title?: string }>({
      query: ({ id, ...body }) => ({ url: `admin/support-cases/${id}/notify-tenant`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'SupportCase', id }],
    }),

    // ── Global search ──
    globalSearch: builder.query<
      { term: string; shape: string; hits: Array<{ kind: string; id: string; label: string; sublabel?: string; tenantId?: string | null; tenantName?: string | null; link?: string }> },
      string
    >({
      query: (q) => `admin/search?q=${encodeURIComponent(q)}`,
    }),

    // ── Announcements ──
    broadcastAnnouncement: builder.mutation<
      any,
      { message: string; title?: string; tenantIds?: string[]; type?: string; expiresInHours?: number; dryRun?: boolean }
    >({
      query: (body) => ({ url: 'admin/announcements', method: 'POST', body }),
    }),
  }),
});

export const {
  useGrantImpersonationMutation,
  useListImpersonationsQuery,
  useRevokeImpersonationMutation,
  useReconcileStockLedgerMutation,
  useUnstickPurchaseOrderMutation,
  useReversePaymentMutation,
  useReleasePrescriptionMutation,
  useRepriceVariantMutation,
  useGrantKycExemptionMutation,
  useListKycExemptionsQuery,
  useRevokeKycExemptionMutation,
  useListSupportCasesQuery,
  useGetSupportCaseQuery,
  useCreateSupportCaseMutation,
  useUpdateSupportCaseMutation,
  useAddCaseNoteMutation,
  useLinkCaseRequestIdMutation,
  useNotifyTenantFromCaseMutation,
  useGlobalSearchQuery,
  useLazyGlobalSearchQuery,
  useBroadcastAnnouncementMutation,
} = adminSupportApi;
